import PrintSettingsCache from "../../../components/printSettings/cache/PrintSettingsCache";
import { PrinterPaperNames, PrintMethods } from "../../enums";
import print from "print-js";
import PrintService from "../../../services/PrintService";
import { BadgeBuilder } from "../../badges/builder/BadgeBuilder";
import { PrintSettingsCacheValue } from "../../../components/printSettings/types";
import { useSnackbar } from "notistack";
import { ZPLBadge } from "../../badges/builder/types";
import { DownloadFileTypes, PAPER_SIZE } from "@reedexpo/mercury-badge";
import { useTranslation } from "react-i18next";
import { SNACKBAR_VARIANTS } from "../../constants";
import { BadgeDownloadOptions } from "../../../components/search/details/download/types";
import EventHelper from "../../network/helpers/event/EventHelper";
import GlobalErrorHandler from "../../utils/errorHandlers/GlobalErrorHandler";

enum PrintErrorMessages {
  TIMEOUT = "Print server connection timeout",
  WEBSOCKET_FAILED = "Print server WebSocket connection failed",
  PRINTER_NOT_AVAILABLE = "Selected printer not available",
  BADGE_GENERATION_FAILED = "Badge generation failed",
  PRINTING_FAILED = "Printing failed"
}

const errorHandler = GlobalErrorHandler.getInstance();

export const useBadgePrint = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("common");

  const printWithServer = async (badge: BadgeBuilder, cachedOptions: PrintSettingsCacheValue, options: BadgeDownloadOptions = {}, paperSize?: PAPER_SIZE) => {
    let serverConnected = false;
    
    try {
      serverConnected = await PrintService.isPrintServerConnected();
      console.info('Print server status check:', { connected: serverConnected });
      
      if (!serverConnected) {
        console.info('Attempting to connect to print server...');
        await PrintService.setupPdfWS({ useCache: true });
        serverConnected = await PrintService.isPrintServerConnected();
        console.info('Print server connection attempt result:', { connected: serverConnected });
      }
      
      const printContext = {
        printMethod: cachedOptions.printMethod,
        printerName: cachedOptions.settingsValues.selectedPrinter,
        paperSize: paperSize?.toString() || 'unknown',
        fileFormat: cachedOptions.settingsValues.printFileFormat?.toString(),
        serverConnected,
        badgeGenerated: !!badge.generatedBadge
      };
      
      console.info('Starting print operation with context:', printContext);
      
      if (+cachedOptions.settingsValues.printFileFormat === DownloadFileTypes.PDF) {
        await PrintService.print(badge.generatedBadge, {
          selectedPrinter: cachedOptions.settingsValues.selectedPrinter,
          // MER-9337
          paperName: EventHelper.isIndiaBu() && paperSize ? PrinterPaperNames[paperSize] : undefined
        });
        console.info('PDF print operation completed successfully');
      } else {
        await PrintService.printZPL((badge.generatedBadge as ZPLBadge)?.z64, { 
          selectedPrinter: cachedOptions.settingsValues.selectedPrinter 
        });
        console.info('ZPL print operation completed successfully');
      }
      
      if (!options.hideToastMessage) {
        enqueueSnackbar(<b>{t("common.badgeSentToPrinter")}</b>, SNACKBAR_VARIANTS.success);
      }
    } catch (e) {
      const error = e as Error;
      console.error("Print server error:", error);
      
      // Enhanced error reporting with detailed context
      errorHandler.handleBadgePrintingError(error, {
        operation: 'printWithServer',
        printMethod: cachedOptions.printMethod,
        printerName: cachedOptions.settingsValues.selectedPrinter,
        badgeId: (badge.badgeInfo as any)?.id || 'unknown',
        paperSize: paperSize?.toString() || 'unknown',
        fileFormat: cachedOptions.settingsValues.printFileFormat?.toString(),
        serverConnected
      });
      
      // Provide more specific error messages based on the error type
      let userErrorMessage = t("errors.unableToConnectToPrintServer");
      
      if (error.message.includes('timeout')) {
        userErrorMessage = t("errors.printServerTimeout") || PrintErrorMessages.TIMEOUT;
      } else if (error.message.includes('WebSocket')) {
        userErrorMessage = t("errors.printServerWebSocketError") || PrintErrorMessages.WEBSOCKET_FAILED;
      } else if (error.message.includes('printer')) {
        userErrorMessage = t("errors.printerNotFound") || PrintErrorMessages.PRINTER_NOT_AVAILABLE;
      } else if (!badge.generatedBadge) {
        userErrorMessage = t("errors.badgeGenerationFailed") || PrintErrorMessages.BADGE_GENERATION_FAILED;
      }
      
      enqueueSnackbar(<b>{userErrorMessage}</b>, SNACKBAR_VARIANTS.error);
      
      // Re-throw for upstream handling if needed
      throw error;
    }
  };

  const printBadge = async (badge: BadgeBuilder, options: BadgeDownloadOptions = {}, paperSize: PAPER_SIZE) => {
    console.info('Print badge operation started:', {
      hasBadge: !!badge,
      hasGeneratedBadge: !!badge?.generatedBadge,
      paperSize,
      options
    });
    
    if (!badge.generatedBadge) {
      const error = new Error('Badge generation failed - no generated badge available');
      
      errorHandler.handleBadgePrintingError(error, {
        operation: 'printBadge',
        badgeId: (badge.badgeInfo as any)?.id || 'unknown',
        paperSize: paperSize?.toString() || 'unknown'
      });
      
      enqueueSnackbar(<b>{t("errors.genericClientError")}</b>, SNACKBAR_VARIANTS.error);
      return;
    }
    
    const cachedOptions = PrintSettingsCache.get();
    console.info('Print options loaded:', {
      printMethod: cachedOptions.printMethod,
      printerName: cachedOptions.settingsValues?.selectedPrinter,
      fileFormat: cachedOptions.settingsValues?.printFileFormat
    });
    
    try {
      if (cachedOptions.printMethod === PrintMethods.PRINT_DIRECT) {
        console.info('Using direct print method');
        print(URL.createObjectURL(badge.generatedBadge as Blob));
        console.info('Direct print operation completed');
        
        // Record successful direct print
        errorHandler.recordBadgePrintingSuccess({
          operation: 'printBadge',
          printMethod: cachedOptions.printMethod,
          badgeId: (badge.badgeInfo as any)?.id || 'unknown',
          paperSize: paperSize?.toString() || 'unknown'
        });
      } else if (cachedOptions.printMethod === PrintMethods.PRINT_SERVER) {
        console.info('Using print server method');
        await printWithServer(badge, cachedOptions, options, paperSize);
        console.info('Print server operation completed');
        
        // Record successful server print
        errorHandler.recordBadgePrintingSuccess({
          operation: 'printBadge',
          printMethod: cachedOptions.printMethod,
          printerName: cachedOptions.settingsValues?.selectedPrinter,
          badgeId: (badge.badgeInfo as any)?.id || 'unknown',
          paperSize: paperSize?.toString() || 'unknown',
          fileFormat: cachedOptions.settingsValues?.printFileFormat?.toString()
        });
      } else {
        throw new Error(`Unknown print method: ${cachedOptions.printMethod}`);
      }
    } catch (e) {
      const error = e as Error;
      console.error('Badge printing failed:', error);
      
      errorHandler.handleBadgePrintingError(error, {
        operation: 'printBadge',
        printMethod: cachedOptions.printMethod,
        printerName: cachedOptions.settingsValues?.selectedPrinter,
        badgeId: (badge.badgeInfo as any)?.id || 'unknown',
        paperSize: paperSize?.toString() || 'unknown',
        fileFormat: cachedOptions.settingsValues?.printFileFormat?.toString()
      });
      
      // Don't show duplicate error messages if printWithServer already showed one
      if (cachedOptions.printMethod === PrintMethods.PRINT_DIRECT) {
        enqueueSnackbar(<b>{t("errors.printingFailed") || PrintErrorMessages.PRINTING_FAILED}</b>, SNACKBAR_VARIANTS.error);
      }
    }
  };

  return { printBadge };
};
